import { useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  MapPin,
  Mountain,
  ScrollText,
  Building2,
  ArrowUpRight,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import StickyCTA from "@/components/site/StickyCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useReveal } from "@/lib/reveal";
import { COMMUNES } from "@/data/communes";

export default function CommunePage({ slug }) {
  useReveal();
  const data = COMMUNES[slug];

  // Helpers grammaticaux : "Le Tampon" → "au Tampon" / "du Tampon" au lieu de "à Le Tampon" / "de Le Tampon"
  const aPrefix = (name) =>
    name === "Le Tampon" ? "au Tampon" : `à ${name}`;
  const dePrefix = (name) =>
    name === "Le Tampon" ? "du Tampon" : `de ${name}`;
  const aName = data ? aPrefix(data.name) : "";
  const deName = data ? dePrefix(data.name) : "";

  useEffect(() => {
    if (!data) return;
    document.title = `Permis de construire & étude de sol ${aPrefix(data.name)} | Solmis Habitat`;
    const description = `Bureau d'études ${aPrefix(data.name)} (974) : permis de construire, étude de sol G1 à G5, accompagnement complet. +100 projets réalisés à La Réunion. Devis gratuit sous 24h.`;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
    window.scrollTo(0, 0);
  }, [data]);

  if (!data) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen" data-testid={`commune-page-${data.slug}`}>
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 bg-[hsl(var(--brand-beige))]">
        <div className="container-wide grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule" />
              <span className="overline">
                {data.region} · {data.tagline}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-medium leading-[1.08] text-primary">
              Bureau d'études & permis de construire{" "}
              {data.name === "Le Tampon" ? (
                <>
                  au{" "}
                  <span className="italic font-light text-[hsl(var(--brand-terra))]">
                    Tampon
                  </span>
                </>
              ) : (
                <>
                  à{" "}
                  <span className="italic font-light text-[hsl(var(--brand-terra))]">
                    {data.name}
                  </span>
                </>
              )}{" "}
              — Solmis Habitat
            </h1>
            <p className="mt-7 max-w-3xl text-base md:text-lg leading-relaxed text-primary/85">
              {data.intro}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="#contact"
                data-testid={`commune-hero-cta-devis-${data.slug}`}
                className="btn-primary group"
              >
                Demander mon devis gratuit
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
              <Link
                to="/#callback"
                className="btn-outline group"
                data-testid={`commune-hero-cta-rappel-${data.slug}`}
              >
                <Phone size={16} strokeWidth={1.6} />
                Être rappelé sous 24h
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 reveal">
            <div className="border border-border bg-white p-7">
              <p className="overline mb-3">Secteurs couverts</p>
              <ul className="flex flex-wrap gap-2">
                {data.secteurs.map((s) => (
                  <li
                    key={s}
                    className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] border border-border rounded-full text-primary/80"
                  >
                    {s}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-border flex items-start gap-2.5 text-sm text-muted-foreground">
                <MapPin
                  size={16}
                  strokeWidth={1.5}
                  className="mt-0.5 text-[hsl(var(--brand-terra))]"
                />
                {data.mairie}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES À LA COMMUNE */}
      <section className="py-20 md:py-28">
        <div className="container-wide grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule" />
              <span className="overline">Nos services {aName}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight text-primary">
              Trois expertises,{" "}
              <span className="italic font-light">un seul interlocuteur</span>.
            </h2>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-md">
              Étude de sol, dossier de permis de construire et accompagnement
              global de votre projet {aName} — du premier rendez-vous à la
              livraison.
            </p>
          </div>

          <div className="md:col-span-7 reveal grid sm:grid-cols-3 gap-4">
            {[
              {
                icon: Mountain,
                title: "Étude de sol",
                desc: "G1 à G5, dimensionnement assainissement, adapté aux sols spécifiques " + deName + ".",
              },
              {
                icon: ScrollText,
                title: "Permis de construire",
                desc: "Dossier conforme au PLU " + deName + ", déposé et suivi en mairie jusqu'à obtention.",
              },
              {
                icon: Building2,
                title: "Accompagnement A → Z",
                desc: "Pilotage complet du projet, coordination des intervenants techniques.",
              },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  className="border border-border bg-white p-7 hover:-translate-y-1 transition-all duration-300"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <Icon
                    size={22}
                    strokeWidth={1.5}
                    className="text-[hsl(var(--brand-terra))]"
                  />
                  <h3 className="mt-4 font-display text-xl text-primary leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GÉOLOGIE & PLU */}
      <section className="py-20 md:py-28 bg-[hsl(var(--brand-beige))]">
        <div className="container-wide grid md:grid-cols-2 gap-10">
          <div className="reveal border border-border bg-white p-8 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <Mountain
                size={22}
                strokeWidth={1.5}
                className="text-[hsl(var(--brand-terra))]"
              />
              <span className="overline">Géologie & terrain</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-primary leading-snug">
              Spécificités des sols {aName}
            </h3>
            <p className="mt-5 text-base text-primary/85 leading-relaxed">
              {data.geology}
            </p>
          </div>
          <div className="reveal border border-border bg-white p-8 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <ScrollText
                size={22}
                strokeWidth={1.5}
                className="text-[hsl(var(--brand-terra))]"
              />
              <span className="overline">PLU & urbanisme</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-primary leading-snug">
              Règles d'urbanisme {aName}
            </h3>
            <p className="mt-5 text-base text-primary/85 leading-relaxed">
              {data.plu}
            </p>
          </div>
        </div>
      </section>

      {/* POURQUOI NOUS À LA COMMUNE */}
      <section className="py-20 md:py-28">
        <div className="container-wide">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-6">
                <span className="rule" />
                <span className="overline">Pourquoi Solmis Habitat {aName}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-medium leading-tight text-primary">
                Une expertise{" "}
                <span className="italic font-light">ancrée localement</span>.
              </h2>
            </div>
            <div className="md:col-span-5 reveal">
              <p className="text-base text-muted-foreground leading-relaxed">
                Plus de 100 projets accompagnés sur l'île. Notre maîtrise du
                territoire {deName} et des règles applicables fait gagner
                du temps et évite les mauvaises surprises.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {data.pourquoi.map((p, i) => (
              <div
                key={p.title}
                className="reveal border border-border bg-white p-7 hover:-translate-y-1 transition-all duration-300"
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`commune-pourquoi-${data.slug}-${i}`}
              >
                <CheckCircle2
                  size={22}
                  strokeWidth={1.5}
                  className="text-[hsl(var(--brand-terra))]"
                />
                <h3 className="mt-4 font-display text-xl text-primary leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ LOCALE */}
      <section className="py-20 md:py-28 bg-[hsl(var(--brand-beige))]">
        <div className="container-narrow grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="rule" />
              <span className="overline">FAQ — {data.name}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight text-primary">
              Questions spécifiques{" "}
              <span className="italic font-light">{aName}</span>.
            </h2>
            <p className="mt-5 text-sm text-muted-foreground">
              Une question qui ne figure pas ici ? Contactez-nous, nous
              répondons sous 24h.
            </p>
          </div>
          <div className="md:col-span-7 reveal">
            <Accordion
              type="single"
              collapsible
              className="border-t border-border bg-white"
            >
              {data.faq.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  data-testid={`commune-faq-${data.slug}-${i}`}
                  className="border-b border-border px-5"
                >
                  <AccordionTrigger className="py-5 text-left font-display text-lg md:text-xl text-primary hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section id="contact" className="py-20 md:py-28">
        <div className="container-wide">
          <div className="reveal border border-border bg-white p-10 md:p-14 text-center">
            <p className="overline mb-4">Votre projet {aName}</p>
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight text-primary max-w-3xl mx-auto">
              Demandez un devis gratuit pour votre projet{" "}
              <span className="italic font-light">{aName}</span>.
            </h2>
            <p className="mt-5 text-base text-muted-foreground max-w-2xl mx-auto">
              Réponse sous 24h ouvrées · Devis 100% gratuit & sans engagement ·
              +100 projets accompagnés sur toute l'île
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/#contact"
                data-testid={`commune-final-cta-devis-${data.slug}`}
                className="btn-primary group"
              >
                Demander mon devis gratuit
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-[hsl(var(--brand-terra))] transition-colors"
              >
                Découvrir tous nos services
                <ArrowUpRight size={16} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}
